import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import copilotStore from "Stores/copilotStore";
import { agentsData } from "Stores/agentsData";
import { AgentOrb, cleanSearchName, orbMode, runWhen, visibleSteps, whenLabel } from "Pages/Dashboard/agents/AgentCard";
import { liveCard, notReady, plain, runAgentAction, scheduleOf } from "./blocks/CopilotAgentCard";
import { stepLabel } from "Pages/Dashboard/agents/stepNames";

/**
 * An agent opened from a card, inside the copilot (same slot as the entity and sequence panels).
 * Everything is read from and acted on through agentsData, so the cards in the chat update with it.
 */

const STATE_LABEL = {
  RUNNING: "Running", WAITING_APPROVAL: "Needs you", FAILING: "Failing",
  ACTIVE: "Live", PAUSED: "Paused", DRAFT: "Draft", ARCHIVED: "Archived"
};
const NOTIFY = { DAILY_DIGEST: "Daily digest", EVERY_RUN: "After every run", ON_FAILURE: "Only when a run fails", IMPORTANT_ONLY: "Only when a run fails", OFF: "Off" };
const RUN_STATUS = { SUCCEEDED: "Succeeded", PARTIAL: "Partly done", FAILED: "Failed", CANCELLED: "Stopped", RUNNING: "Running", QUEUED: "Starting", WAITING_APPROVAL: "Needs you", SLEEPING: "Waiting" };

const prettify = t => String(t || "").split(".").pop().replace(/_/g, " ");

const AgentPanel = observer(() => {
  const history = useHistory();
  const id = copilotStore.agentPanel.agentId;
  const [agent, setAgent] = useState(null);
  const [runs, setRuns] = useState([]);
  const [stepDefs, setStepDefs] = useState(agentsData.stepTypesCache || []);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");

  const load = async () => {
    const [a, r] = await Promise.all([agentsData.peekAgent(id), agentsData.peekRuns(id, 5), agentsData.fetchOverview()]);
    setAgent(a);
    setRuns(r);
  };

  useEffect(() => {
    if (!id) return;
    setAgent(null);
    setRuns([]);
    setNote("");
    load();
    agentsData.fetchStepTypes().then(setStepDefs);
    agentsData.fetchSequencesLite();
    agentsData.fetchSavedSearches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!id) return null;
  const card = liveCard(id, null);
  const a = agent || (card && card.agent);
  const close = copilotStore.closeAgentPanel;

  const act = kind => async () => {
    if (busy || !a) return;
    if (kind === "on" && notReady(a)) {
      setNote(notReady(a));
      return;
    }
    setBusy(kind);
    setNote("");
    try {
      // Stay in the chat: the new run lands in Recent runs and the cards refresh with it.
      await runAgentAction(kind, id);
      await load();
    } finally {
      setBusy("");
    }
  };

  if (!a) {
    return (
      <div className="copilotTablePanel cpAgentPanel">
        <div className="cpAgentPanel__skel linear-background" />
      </div>
    );
  }

  const state = (card && card.state) || a.status;
  const cfg = a.config || {};
  const stepName = (type, nodeId) => stepLabel(type, stepDefs, a.flow, nodeId);
  const stepDetail = n => {
    const def = (stepDefs || []).find(d => d.type === n.stepType);
    const vals = n.params || {};
    const parts = [];
    ((def && def.params) || []).forEach(p => {
      const v = vals[p.key];
      if (v === undefined || v === null || v === "" || v === false || (Array.isArray(v) && !v.length)) return;
      const opt = (p.options || []).find(o => String(o.value) === String(v));
      const seq = (agentsData.sequencesLite || []).find(x => x.value === v);
      const ss = (agentsData.savedSearches || []).find(x => x.id === v);
      const val = v === "{{ask}}" ? "asked each run" : v === true ? "" : opt ? opt.label : seq ? seq.label : ss ? cleanSearchName(ss.label)
        : Array.isArray(v) ? `${v.length} selected` : typeof v === "object" || /^[0-9a-f]{24}$/i.test(String(v)) ? null : String(v);
      if (val !== null) parts.push(val ? `${p.label}: ${val}` : p.label);
    });
    return parts.slice(0, 2).join(" · ");
  };

  return (
    <div className="copilotTablePanel cpAgentPanel">
      <div className="copilotTablePanel__header cpAgentPanel__head">
        <AgentOrb mode={a.status === "DRAFT" && state !== "RUNNING" ? "draft" : orbMode(state)} />
        <div className="copilotTablePanel__headMeta">
          <div className="copilotTablePanel__title">{a.name || "Untitled agent"}</div>
          <div className="copilotTablePanel__count">
            <span className={`cpAgentCard__pill cpAgentCard__pill--${String(state).toLowerCase()}`}>{STATE_LABEL[state] || "Draft"}</span>
          </div>
        </div>
        <span className="material-icons-outlined copilotTablePanel__close" onClick={close}>close</span>
      </div>

      <div className="copilotTablePanel__body cpAgentPanel__body">
        <div className="cpAgentPanel__actions">
          <button type="button" className="ag-btn ag-btn--sm" disabled={!!busy} onClick={act("test")}>
            <i className="material-icons-outlined">visibility</i>{busy === "test" ? "Testing…" : "Test run"}
          </button>
          {a.status === "ACTIVE" && (
            <button type="button" className="ag-btn ag-btn--sm" disabled={!!busy} onClick={act("run")}>
              <i className="material-icons-outlined">play_arrow</i>{busy === "run" ? "Starting…" : "Run now"}
            </button>
          )}
          {a.status === "ACTIVE" ? (
            <button type="button" className="ag-btn ag-btn--sm" disabled={!!busy} onClick={act("pause")}>
              <i className="material-icons-outlined">pause</i>{busy === "pause" ? "Pausing…" : "Pause"}
            </button>
          ) : (
            <button type="button" className="ag-btn ag-btn--sm ag-btn--primary" disabled={!!busy} onClick={act("on")}>
              <i className="material-icons-outlined">{a.status === "PAUSED" ? "play_arrow" : "power_settings_new"}</i>
              {busy === "on" ? "Turning on…" : a.status === "PAUSED" ? "Resume" : "Turn on"}
            </button>
          )}
          <button type="button" className="ag-link cpAgentPanel__full" onClick={() => history.push(`/agents/${id}`)}>
            Open full page<i className="material-icons-outlined">open_in_new</i>
          </button>
        </div>
        {note && <div className="cpAgentCard__note">{note}</div>}

        {a.summary && <p className="cpAgentPanel__summary">{plain(a.summary)}</p>}

        <div className="cpAgentPanel__facts">
          <div><span>Runs</span><b>{scheduleOf(a).replace(/^Runs /, "").replace(/^./, c => c.toUpperCase())}</b></div>
          {a.status === "ACTIVE" && card && card.nextRunAt && <div><span>Next run</span><b>{whenLabel(card.nextRunAt)}</b></div>}
          <div><span>Approval</span><b>{cfg.approvalMode === "REQUIRED" ? "You approve every run" : "First run only"}</b></div>
          {!!cfg.dailyRecordCap && <div><span>Per run</span><b>Up to {cfg.dailyRecordCap} contacts</b></div>}
          <div><span>Notifications</span><b>{NOTIFY[cfg.notifyMode] || "Off"}</b></div>
        </div>

        <div className="cpAgentPanel__section">Recent runs</div>
        {runs.length === 0 ? (
          <div className="cpAgentPanel__empty">No runs yet. Test it to see who it would add.</div>
        ) : (
          <ul className="cpAgentPanel__runs">
            {runs.map(r => (
              <li key={r.id}>
                <button type="button" onClick={() => history.push(`/agents/${id}/runs/${r.id}`)}>
                  <span className="cpAgentPanel__runWhen">{runWhen(r.startedAt)}</span>
                  {r.dryRun && <span className="cpAgentCard__pill cpAgentCard__pill--draft">Test run</span>}
                  <span className="cpAgentPanel__runStat">{RUN_STATUS[r.status] || prettify(r.status)}</span>
                  <span className="cpAgentPanel__runNum">{r.enrolledCount || 0} {r.dryRun ? "would add" : "added"}</span>
                  <i className="material-icons-outlined">chevron_right</i>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="cpAgentPanel__section">Steps</div>
        <ol className="cpAgentPanel__steps">
          {visibleSteps(a).map((n, i) => (
            <li key={n.nodeId || i}>
              <b>{stepName(n.stepType, n.nodeId)}</b>
              {!!stepDetail(n) && <span>{stepDetail(n)}</span>}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
});

export default AgentPanel;
